<script>
import { fade } from "svelte/transition";
import { toast } from "@zerodevx/svelte-toast";
import Swal from "sweetalert2";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import * as m from "@app/paraglide/messages.js";

const ipcRenderer = window.ipcRenderer;
const ACTIVATE_PAGE = "https://www.closedcaptioncreator.com/activate.html";
/* 12-month update renewals (Stripe Payment Links, created by create_lifetime_catalog.js); checkout asks for the key. */
const RENEW_LINKS = {
    starter: "https://buy.stripe.com/fZu8wP8r00QAcK6cOofQI0j",
    pro: "https://buy.stripe.com/eVq8wPgXw42MeSeaGgfQI0k",
};

const license = $derived(authState.license || {});
const isLifetime = $derived(license.edition === "lifetime");
const tierLabel = $derived(authState.plan === "pro" ? "Pro" : authState.plan === "enterprise" ? "Enterprise" : "Starter");

let storedKey = $state(localStorage.getItem("cc-license-key") || ""),
    keyInput = $state(""),
    info = $state(null),
    loading = $state(false),
    releasing = $state(false);

function formatDate(value) {
    if (!value) return "—";
    const date = typeof value === "number" ? new Date(value) : new Date(`${value}T00:00:00`);
    return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function errorText(code) {
    switch (code) {
        case "invalid_key":
        case "not_found":
            return m.license_error_not_found();
        case "transfer_limit":
            return m.license_error_transfer_limit();
        case "network":
            return m.license_error_network();
        case "rate_limited":
            return m.license_error_rate_limited();
        default:
            return m.license_error_generic();
    }
}

async function loadInfo() {
    if (!isLifetime || !storedKey) return;
    loading = true;
    const res = await ipcRenderer.invoke("licenseApi", { action: "info", licenseKey: storedKey });
    loading = false;
    if (res.license) info = res.license;
}

async function releaseThisComputer() {
    const key = storedKey || keyInput.trim();
    if (!key) return;
    const confirm = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        icon: "warning",
        title: m.license_release_confirm_title(),
        text: m.license_release_confirm_text(),
        showCancelButton: true,
        confirmButtonText: m.license_release_button(),
        cancelButtonText: m.cancel(),
        focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    releasing = true;
    const res = await ipcRenderer.invoke("licenseApi", { action: "deactivate", licenseKey: key });
    releasing = false;
    if (res.error) {
        toast.push(errorText(res.error), { classes: ["toast-danger"] });
        return;
    }
    localStorage.removeItem("cc-offline-license");
    localStorage.removeItem("cc-license-key");
    toast.push(m.license_released_toast(), { classes: ["toast-success"] });
    window.location.reload();
}

function openActivatePage() {
    ipcRenderer.invoke("openExternal", ACTIVATE_PAGE);
}

function openRenewal() {
    if (storedKey) navigator.clipboard?.writeText(storedKey).catch(() => {});
    if (storedKey) toast.push(m.license_key_copied_for_renewal(), { classes: ["toast-info"] });
    ipcRenderer.invoke("openExternal", RENEW_LINKS[authState.plan] || RENEW_LINKS.starter);
}

loadInfo();
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'licenseInfo' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="licenseInfoTitle"
    aria-modal="true"
    tabindex="-1"
    id="LicenseInfoModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="licenseInfoTitle"><i class="bi bi-key me-2"></i>{m.license_title()}</h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <div class="license-summary mb-3">
                    <div class="fw-semibold fs-5">
                        Closed Caption Creator {tierLabel}
                        <span class="badge ms-1 {isLifetime ? 'text-bg-primary' : 'text-bg-secondary'}">
                            {isLifetime ? m.license_edition_lifetime() : m.license_edition_subscription()}
                        </span>
                    </div>
                    <dl class="row small mb-0 mt-2">
                        {#if isLifetime}
                            <dt class="col-6 fw-normal text-body-secondary">{m.license_updates_until()}</dt>
                            <dd class="col-6 mb-1">{formatDate(info?.maintenanceUntil || license.maintenanceUntil)}</dd>
                        {:else if license.expires}
                            <dt class="col-6 fw-normal text-body-secondary">{m.license_expires()}</dt>
                            <dd class="col-6 mb-1">{formatDate(license.expires)}</dd>
                        {/if}
                        <dt class="col-6 fw-normal text-body-secondary">{m.license_version()}</dt>
                        <dd class="col-6 mb-1">{environment.version}</dd>
                    </dl>
                </div>

                {#if isLifetime}
                    <div class="form-label small fw-semibold mb-1">{m.license_computers()}</div>
                    {#if loading}
                        <div class="small text-body-secondary"><span class="spinner-border spinner-border-sm me-1"></span>{m.license_loading()}</div>
                    {:else if info}
                        <ul class="list-group list-group-flush border rounded mb-2">
                            {#each info.activations as activation (activation.mid)}
                                <li class="list-group-item d-flex justify-content-between align-items-center small">
                                    <span><i class="bi bi-pc-display me-2"></i>{activation.name}</span>
                                    {#if activation.current}<span class="badge text-bg-success">{m.license_this_computer()}</span>{/if}
                                </li>
                            {/each}
                        </ul>
                        <div class="small text-body-secondary mb-3">
                            {m.license_slots({ used: info.activations.length, max: info.maxMachines })} · {m.license_transfers_left({ count: info.transfersLeft })}
                        </div>
                    {:else}
                        <p class="small text-body-secondary mb-2">{m.license_machines_offline()}</p>
                        {#if !storedKey}
                            <input type="text" class="form-control form-control-sm mb-3" placeholder="CCC-XXXXX-XXXXX-XXXXX-XXXXX"
                                   aria-label={m.license_key_label()} bind:value={keyInput} />
                        {/if}
                    {/if}

                    <div class="d-flex flex-wrap gap-2">
                        <button type="button" class="btn btn-primary btn-sm" onclick={openRenewal}>
                            <i class="bi bi-arrow-repeat me-1"></i>{m.license_renew_button()}
                        </button>
                        <button type="button" class="btn btn-outline-secondary btn-sm" onclick={openActivatePage}>
                            <i class="bi bi-box-arrow-up-right me-1"></i>{m.license_manage_button()}
                        </button>
                        <button type="button" class="btn btn-outline-danger btn-sm ms-auto" onclick={releaseThisComputer}
                                disabled={releasing || !(storedKey || keyInput.trim())}>
                            {#if releasing}<span class="spinner-border spinner-border-sm me-1"></span>{/if}
                            {m.license_release_button()}
                        </button>
                    </div>
                {:else}
                    <p class="small text-body-secondary mb-0">{m.license_subscription_note()}</p>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    .license-summary {
        padding: 1rem;
        border-radius: 0.5rem;
        background: var(--bs-tertiary-bg);
        border: 1px solid var(--bs-border-color);
    }
</style>
